'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Select, SelectItem } from '@nextui-org/react';

import { PageWrapper } from '@/components/layout/page-wrapper/page-wrapper';
import { MasterclassCard } from '@/components/masterclass/masterclass-card';

import { useGetFilteredMcs } from '@/widgets/masterclasses/hooks/useGetFilteredMcs';

import { MscPageHeader } from '@/widgets/masterclasses/msc-page-header';

import { SearchEmpty } from '@/widgets/empty-states/search-empty';

import Image from 'next/image';
import { ArrowUpToLine } from 'lucide-react';
import {
  useGetCategoriesAsOptionsQuery,
  type ICategoryOption,
} from '@/entities/categories';
import { IMasterclass } from '@/entities/masterclass/types';
import { MASTERCLASS_SORT_OPTIONS } from '@/widgets/masterclasses/constants/sort-options';
import { ROUTES } from '@/shared/routes/routes';
import { Link } from '@/shared/navigation';
import { cn } from '@/shared/utils/utils';

/** Как в parkaeva `CatalogProductGrid` — чанк дозагрузки при скролле */
const INCREMENTAL_PAGE_SIZE = 16;

/**
 * Ссылка одного таба категории — настоящий next/link на
 * /masterclasses/category/[value] (краулер видит реальные ссылки для
 * перелинковки и индексации). Next.js App Router сам делает клиентский
 * переход без полной перезагрузки страницы.
 */
const CategoryTabLink = ({
  href,
  isSelected,
  label,
  count,
}: {
  href: string;
  isSelected: boolean;
  label: React.ReactNode;
  count: number;
}) => (
  <Link
    href={href}
    role="tab"
    aria-selected={isSelected}
    data-mc-category-tab={isSelected ? 'true' : undefined}
    className={cn(
      'z-0 flex h-9 shrink-0 cursor-pointer items-center justify-center gap-1.5',
      'whitespace-nowrap rounded-full px-3 text-medium outline-none transition-colors',
      'tap-highlight-transparent',
      isSelected
        ? 'bg-background text-default-foreground shadow-small dark:bg-default'
        : 'text-default-500 hover:opacity-80',
    )}
  >
    {label}
    {/* default-600, не 400: контраст счётчика ≥ 4.5:1 в обеих темах (a11y) */}
    <span className="text-default-600">{count}</span>
  </Link>
);

/**
 * Единый список табов категорий. Рендерится дважды (мобильная sticky-панель и
 * десктопный блок) — скрытая версия убрана из accessibility-дерева через
 * display:none (md:hidden / hidden md:block).
 */
const CategoryTabs = ({
  selectedKey,
  categories,
  categoryCounts,
  totalCount,
  className,
}: {
  selectedKey: string | null;
  categories: ICategoryOption[] | undefined;
  categoryCounts: Map<number, number>;
  totalCount: number;
  className?: string;
}) => (
  <div
    role="tablist"
    className={cn(
      'inline-flex w-fit flex-nowrap items-center gap-2 overflow-x-scroll rounded-large bg-default-100 p-1 scrollbar-hide',
      className,
    )}
  >
    <CategoryTabLink
      href={ROUTES.masterclasses}
      isSelected={(selectedKey ?? 'all') === 'all'}
      label="Все"
      count={totalCount}
    />
    {categories?.map((category) => (
      <CategoryTabLink
        key={category.id}
        href={ROUTES.masterclassesCategory.replace(':value', category.value)}
        isSelected={selectedKey === `${category.id}`}
        label={category.label}
        count={categoryCounts.get(category.id) ?? 0}
      />
    ))}
  </div>
);

const DEFAULT_TITLE = 'Мастер-классы по вязанию игрушек амигуруми крючком';
const DEFAULT_SUBTITLE = 'Пошаговые схемы и описания — для начинающих и опытных';

export default function MasterclassesContent({
  masterclasses: initialMasterclasses,
  categoryOptions: initialCategoryOptions,
  initialCategoryId = 'all',
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
}: {
  masterclasses: IMasterclass[];
  categoryOptions: ICategoryOption[];
  /** Категория из URL /masterclasses/category/[value] — открывает нужный таб сразу, без мигания. */
  initialCategoryId?: string;
  /** Заголовок страницы — свой на /masterclasses/category/[value] (напр. «Подушки крючком»). */
  title?: string;
  subtitle?: string;
}) {
  const {
    masterclasses,
    searchValue,
    onSortChange,
    sortValue,
    onSearchChange,
    categoryId,
  } = useGetFilteredMcs(initialMasterclasses, initialCategoryId);

  const [visibleCount, setVisibleCount] = useState(INCREMENTAL_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const mobileTabsRef = useRef<HTMLDivElement>(null);
  const desktopTabsRef = useRef<HTMLDivElement>(null);

  const scrollActiveTabIntoView = (container: HTMLDivElement | null) => {
    if (!container) {
      return;
    }

    const activeTab = container.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]',
    );

    activeTab?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  const incrementalLoad = (masterclasses?.length ?? 0) > INCREMENTAL_PAGE_SIZE;
  const limit = incrementalLoad ? visibleCount : (masterclasses ?? []).length;
  const items = useMemo(() => {
    return (masterclasses ?? []).slice(0, limit);
  }, [masterclasses, limit]);

  const hasMore = incrementalLoad && limit < (masterclasses ?? []).length;

  useEffect(() => {
    setVisibleCount(INCREMENTAL_PAGE_SIZE);
  }, [categoryId, sortValue, searchValue]);

  useEffect(() => {
    if (!incrementalLoad || !hasMore || !sentinelRef.current) {
      return;
    }
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return;
        }
        setVisibleCount((prev) =>
          Math.min(prev + INCREMENTAL_PAGE_SIZE, masterclasses.length),
        );
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [incrementalLoad, hasMore, masterclasses.length]);

  const sortSelectItems = MASTERCLASS_SORT_OPTIONS;

  const { data: categories } = useGetCategoriesAsOptionsQuery({
    initialCategoryOptions,
  });

  const categoryCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const mc of initialMasterclasses) {
      for (const category of mc.categories ?? []) {
        counts.set(category.id, (counts.get(category.id) ?? 0) + 1);
      }
    }
    return counts;
  }, [initialMasterclasses]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      scrollActiveTabIntoView(mobileTabsRef.current);
      scrollActiveTabIntoView(desktopTabsRef.current);
    });

    return () => cancelAnimationFrame(frameId);
  }, [categoryId, categories]);

  const showEndOfList =
    masterclasses.length > 0 &&
    !hasMore &&
    items.length === masterclasses.length &&
    masterclasses.length > INCREMENTAL_PAGE_SIZE;

  return (
    <>
      <PageWrapper className="relative">
        <div className="px-1 pb-1 pt-2 md:hidden">
          <h1 className=" font-bold text-foreground">{title}</h1>
          <p className="text-sm text-default-500">{subtitle}</p>
        </div>
        <div
          ref={mobileTabsRef}
          className="sticky top-[64px] z-40 -mx-4 px-4 pb-2 pt-2 md:hidden"
        >
          <CategoryTabs
            className="w-full"
            selectedKey={categoryId}
            categories={categories}
            categoryCounts={categoryCounts}
            totalCount={initialMasterclasses.length}
          />
        </div>
        <div className="relative flex w-full justify-between gap-4 md:hidden">
          <Select
            value={sortValue}
            onChange={(e) => onSortChange(e.target.value)}
            size="sm"
            radius="full"
            label="Сортировка"
            defaultSelectedKeys={['default']}
            className="w-[210px]"
          >
            {sortSelectItems.map((item) => (
              <SelectItem key={item.key}>{item.label}</SelectItem>
            ))}
          </Select>
          <Image
            className="absolute -top-[15px] -right-[5px]"
            src="/icon/scroll-tabs.png"
            width={60}
            height={60}
            alt={'scroll'}
          />
        </div>
        <MscPageHeader
          title={title}
          subtitle={subtitle}
          searchValue={searchValue}
          setSearchValue={onSearchChange}
          sortValue={sortValue}
          // @ts-ignore
          setSortValue={onSortChange}
        />{' '}
        <div ref={desktopTabsRef} className="hidden md:block">
          <CategoryTabs
            selectedKey={categoryId}
            categories={categories}
            categoryCounts={categoryCounts}
            totalCount={initialMasterclasses.length}
          />
        </div>
        <div className="overflow-x-clip">
          <div
            key={categoryId}
            className="mc-category-grid grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6"
          >
            {items?.map((mc, index) => {
              return (
                <MasterclassCard
                  masterclass={mc}
                  key={mc.id}
                  imageHeight={300}
                  headingLevel="h2"
                  /* Первый ряд сетки — кандидаты в LCP: eager + fetchpriority=high */
                  priority={index < 4}
                />
              );
            })}
          </div>
        </div>
        {hasMore && (
          <div ref={sentinelRef} className="h-4 w-full shrink-0" aria-hidden />
        )}
        {Array.isArray(masterclasses) && masterclasses.length === 0 && (
          <SearchEmpty />
        )}
        {showEndOfList && (
          <div className="mt-auto flex w-full flex-col items-center gap-2 border-t-2 border-dashed pt-2">
            Вы дошли до конца списка
            <Button
              radius="full"
              color="primary"
              variant="bordered"
              onClick={() =>
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
              }
            >
              Наверх <ArrowUpToLine strokeWidth={1} size={24} />
            </Button>
          </div>
        )}
      </PageWrapper>
    </>
  );
}
